import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import { cn } from "@arcadeai/design-system/lib/utils";
import { KeyRound, Play } from "lucide-react";
import { motion } from "motion/react";

type TerminalState = "ready" | "loading" | "success" | "error";

type RunButtonProps = {
  onExecute?: () => void;
  isExecuteDisabled?: boolean;
  tooltipMessage?: string | null;
  needsAuthorization?: boolean;
  subtle?: boolean;
};

function RunButton({
  onExecute,
  isExecuteDisabled,
  tooltipMessage,
  needsAuthorization,
  subtle,
}: RunButtonProps) {
  const Icon = needsAuthorization ? KeyRound : Play;
  const label = needsAuthorization ? "Auth & Run" : "Run tool";

  const buttonClass = subtle
    ? "h-6 gap-1.5 text-xs border-neutral-300 text-neutral-600 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800"
    : "h-6 gap-1.5 bg-emerald-600 text-white text-xs hover:bg-emerald-500 dark:bg-emerald-600 dark:hover:bg-emerald-500";

  return (
    <span
      className={cn(isExecuteDisabled && "cursor-not-allowed")}
      title={tooltipMessage ?? undefined}
    >
      <Button
        className={buttonClass}
        disabled={isExecuteDisabled}
        onClick={onExecute}
        size="sm"
        variant={subtle ? "outline" : "default"}
      >
        <Icon className="h-3 w-3" />
        {label}
      </Button>
    </span>
  );
}

type HeaderActionsProps = {
  state: TerminalState;
  responseString?: string;
  onExecute?: () => void;
  isExecuteDisabled?: boolean;
  tooltipMessage?: string | null;
  needsAuthorization?: boolean;
};

export function HeaderActions({
  state,
  responseString,
  onExecute,
  isExecuteDisabled,
  tooltipMessage,
  needsAuthorization,
}: HeaderActionsProps) {
  if (state === "ready") {
    return (
      <RunButton
        isExecuteDisabled={isExecuteDisabled}
        needsAuthorization={needsAuthorization}
        onExecute={onExecute}
        tooltipMessage={tooltipMessage}
      />
    );
  }

  if (state === "loading") {
    return (
      <Badge
        className="border-amber-500/50 bg-amber-100 text-amber-700 dark:border-amber-700/50 dark:bg-amber-950/80 dark:text-amber-400"
        variant="outline"
      >
        <motion.span
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY }}
        >
          Executing
        </motion.span>
      </Badge>
    );
  }

  if (responseString) {
    return (
      <CopyButton
        className="h-7 w-7 bg-neutral-200 text-neutral-600 hover:scale-100 hover:bg-neutral-300 hover:text-neutral-800 active:scale-100 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-neutral-200"
        content={responseString}
        variant="ghost"
      />
    );
  }

  return null;
}
